import { z } from "zod";
import { parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";
import { getFullPhoneNumber } from "@/components/ui/phone-input";

const ORG_SIZES = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1001-5000",
  "5001-10000",
  "10000+",
] as const;

export const aboutStepSchema = z
  .object({
    first_name: z.string().refine((s) => s.trim().length > 0, { message: "First name is required" }),
    last_name: z.string().refine((s) => s.trim().length > 0, { message: "Last name is required" }),
    phone_number: z.string(),
    phone_country_code: z.string(),
    timezone: z.string().refine((s) => s.trim().length > 0, { message: "Select a timezone" }),
  })
  .superRefine((data, ctx) => {
    if (!data.phone_number?.trim()) return;
    const e164 = getFullPhoneNumber(data.phone_country_code as CountryCode, data.phone_number);
    if (!e164) {
      ctx.addIssue({ code: "custom", message: "Enter a valid phone number", path: ["phone_number"] });
      return;
    }
    const p = parsePhoneNumberFromString(e164);
    if (!p?.isValid()) {
      ctx.addIssue({ code: "custom", message: "Enter a valid phone number", path: ["phone_number"] });
    }
  });

export const organizationStepSchema = z.object({
  name: z
    .string()
    .refine((s) => s.trim().length > 0, { message: "Organization name is required" }),
  website: z.string(),
  size: z.enum(ORG_SIZES, { message: "Select organization size" }),
});

const optionalEmployerField = () =>
  z
    .string()
    .refine((s) => s.length <= 800, {
      message: "Must be 800 characters or fewer",
    });

export const employerStepSchema = z.object({
  mission_vision: optionalEmployerField(),
  funding_traction: optionalEmployerField(),
  culture_principles: optionalEmployerField(),
  benefits: optionalEmployerField(),
});

export const customizeStepSchema = z.object({
  role: z.array(z.string()).min(1, { message: "Select a role" }),
  priorities: z.array(z.string()).min(1, { message: "Select at least one priority" }),
  referral_source: z
    .array(z.string())
    .min(1, { message: "Select how you heard about Arbi" }),
});

export const inviteEmailSchema = z.email({
  message: "Please enter a valid email address",
});

export const projectStepSchema = z.object({
  title: z
    .string()
    .refine((s) => s.trim().length > 0, { message: "Project title is required" })
    .refine((s) => s.length <= 100, { message: "Project title is too long" }),
  access_level: z.enum(["shared", "private"], {
    message: "Select an access level",
  }),
  job_title: z
    .string()
    .refine((s) => s.trim().length > 0, { message: "Job title is required" })
    .refine((s) => s.length <= 255, { message: "Job title is too long" }),
});
